import { ProductIdentity } from '@/components/products';
import { MarginBadge } from '@/components/products/MarginBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FormattedNumber } from '@/components/global';
import { formatPlanogramGbp, computeBlendedMargin, uniformPlanogramNumberLabel } from '@/lib/planogramIncome';
import {
  comparePlanogramGroupsBySlotNumber,
  earliestPlanogramSlotNumber,
  formatPlanogramSlotLabel,
  PLANOGRAM_UNASSIGNED_KEY,
  planogramMarginRagSortRank,
  resolvePlanogramMarginPercent,
} from '@/lib/planogramSort';
import { Info } from 'lucide-react';
import { PlanogramSlotBadge } from './PlanogramSlotBadge';
import { PlanogramListingFeeInput } from './PlanogramListingFeeInput';

export interface PlanogramBuilderListSlot {
  slot: {
    id: string;
    slotNumber: number;
    slotLabel: string | null;
    maxCapacity: number;
    sellPriceOverride: number | null;
    listingFee: number;
    productId: string | null;
  };
  product: {
    sku: string;
    name: string;
    imageUrl: string | null;
    media: Array<{ url: string; mediaType: string }> | null;
    purchasePrice: number;
    sellPrice: number;
  } | null;
  cost: number | null;
  effectiveSell: number | null;
  margin: number | null;
}

export interface PlanogramProductGroup {
  key: string;
  productId: string | null;
  productName: string;
  product: PlanogramBuilderListSlot['product'];
  slots: PlanogramBuilderListSlot[];
  totalCapacity: number;
  cost: number | null;
  salePriceLabel: string;
  listingFeeLabel: string;
  blendedMargin: number | null;
  sortRank: number;
}

function formatSlotLabel(entry: PlanogramBuilderListSlot): string {
  return formatPlanogramSlotLabel(entry.slot.slotLabel, entry.slot.slotNumber);
}

function uniformNumberLabel(values: Array<number | null>, format: (n: number) => string): string {
  return uniformPlanogramNumberLabel(values, format);
}

export function groupPlanogramSlotsByProduct(
  slots: PlanogramBuilderListSlot[]
): PlanogramProductGroup[] {
  const map = new Map<string, PlanogramProductGroup>();

  for (const entry of slots) {
    const key = entry.slot.productId ?? PLANOGRAM_UNASSIGNED_KEY;
    const existing = map.get(key);

    if (existing) {
      existing.slots.push(entry);
      existing.totalCapacity += entry.slot.maxCapacity;
    } else {
      const productName =
        key === PLANOGRAM_UNASSIGNED_KEY
          ? 'Empty slots'
          : entry.product?.name?.trim() || entry.product?.sku?.trim() || 'Unknown product';

      map.set(key, {
        key,
        productId: entry.slot.productId,
        productName,
        product: entry.product,
        slots: [entry],
        totalCapacity: entry.slot.maxCapacity,
        cost: entry.cost,
        salePriceLabel: '—',
        listingFeeLabel: '—',
        blendedMargin: null,
        sortRank: planogramMarginRagSortRank(null),
      });
    }
  }

  const groups = Array.from(map.values()).map((group) => {
    const sortedSlots = [...group.slots].sort(
      (a, b) => a.slot.slotNumber - b.slot.slotNumber
    );
    const margins = sortedSlots.map((s) => s.margin);
    const worstRank = Math.min(
      ...sortedSlots.map((s) =>
        planogramMarginRagSortRank(
          resolvePlanogramMarginPercent({
            slotNumber: s.slot.slotNumber,
            slotLabel: s.slot.slotLabel,
            productId: s.slot.productId,
            marginPercent: s.margin,
          })
        )
      )
    );

    return {
      ...group,
      slots: sortedSlots,
      cost: group.productId ? sortedSlots[0]?.cost ?? null : null,
      salePriceLabel: uniformNumberLabel(
        sortedSlots.map((s) => s.effectiveSell),
        formatPlanogramGbp
      ),
      listingFeeLabel: uniformNumberLabel(
        sortedSlots.map((s) => s.slot.listingFee),
        formatPlanogramGbp
      ),
      blendedMargin: computeBlendedMargin(margins),
      sortRank: worstRank,
    };
  });

  groups.sort((a, b) =>
    comparePlanogramGroupsBySlotNumber(
      {
        earliestSlotNumber: earliestPlanogramSlotNumber(
          a.slots.map((entry) => entry.slot)
        ),
        empty: a.key === PLANOGRAM_UNASSIGNED_KEY,
      },
      {
        earliestSlotNumber: earliestPlanogramSlotNumber(
          b.slots.map((entry) => entry.slot)
        ),
        empty: b.key === PLANOGRAM_UNASSIGNED_KEY,
      }
    )
  );

  return groups;
}

function PlanogramSlotPill({
  entry,
  onClick,
}: {
  entry: PlanogramBuilderListSlot;
  onClick: () => void;
}) {
  const isEmpty = !entry.slot.productId;
  const label = formatSlotLabel(entry);
  const ariaLabel = isEmpty ? `Slot ${label}: assign product` : `Slot ${label}: edit`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <PlanogramSlotBadge label={label} muted={isEmpty} />
    </button>
  );
}

export interface PlanogramBuilderProductListProps {
  slots: PlanogramBuilderListSlot[];
  targetMargin: number;
  onSlotClick: (slotId: string) => void;
  onListingFeeChange: (slotIds: string[], raw: string) => void;
}

export function PlanogramBuilderProductList({
  slots,
  targetMargin,
  onSlotClick,
  onListingFeeChange,
}: PlanogramBuilderProductListProps) {
  const groups = groupPlanogramSlotsByProduct(slots);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="min-w-[16rem] pl-5">Product</TableHead>
          <TableHead>Slots</TableHead>
          <TableHead className="w-24 text-right">Capacity</TableHead>
          <TableHead className="w-28 text-right">
            <div className="flex items-center justify-end gap-1">
              <span>Cost price</span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label="About cost price"
                    >
                      <Info className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[16rem] text-left">
                    Unit purchase cost from the assigned product in Inventory. Update the
                    product&apos;s purchase price to change it here.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="w-28 text-right">Sale price</TableHead>
          <TableHead className="w-28 text-right">Listing fee</TableHead>
          <TableHead className="w-32 pr-5 text-right">Margin</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((group) => (
          <TableRow key={group.key}>
            <TableCell className="pl-5">
              {group.productId && group.product ? (
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => onSlotClick(group.slots[0].slot.id)}
                >
                  <ProductIdentity
                    name={group.productName}
                    sku={group.product.sku}
                    size="sm"
                    showSku={false}
                    product={{
                      imageUrl: group.product.imageUrl,
                      media: group.product.media ?? undefined,
                    }}
                  />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <span className="text-xs font-medium">—</span>
                  </div>
                  <p className="text-sm font-medium leading-snug">{group.productName}</p>
                </div>
              )}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {group.slots.map((entry) => (
                  <PlanogramSlotPill
                    key={entry.slot.id}
                    entry={entry}
                    onClick={() => onSlotClick(entry.slot.id)}
                  />
                ))}
              </div>
            </TableCell>
            <TableCell className="text-right">
              <FormattedNumber value={group.totalCapacity} />
            </TableCell>
            <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
              {group.cost != null && group.cost > 0 ? formatPlanogramGbp(group.cost) : '—'}
            </TableCell>
            <TableCell className="text-right text-sm tabular-nums">{group.salePriceLabel}</TableCell>
            <TableCell className="text-right text-sm tabular-nums">
              <PlanogramListingFeeInput
                slotIds={group.slots.map((entry) => entry.slot.id)}
                value={group.slots[0]?.slot.listingFee ?? 0}
                varies={group.listingFeeLabel === 'Varies'}
                disabled={!group.productId}
                onChange={onListingFeeChange}
              />
            </TableCell>
            <TableCell className="pr-5 text-right">
              {group.blendedMargin != null ? (
                <MarginBadge marginPercent={group.blendedMargin} targetMargin={targetMargin} />
              ) : group.productId ? (
                <span
                  className="text-sm text-muted-foreground"
                  title="Set a purchase cost on this product to calculate margin"
                >
                  Cost n/a
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default PlanogramBuilderProductList;
